import React, { useEffect, useState, useRef } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { useRouter } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import {
  UserCreateForm,
  UserCreateFormRef,
  UserForm,
  UserPayload,
  USER_STATUS_OPTIONS,
  getUserStatusColor,
} from "@/components/screens/user/UserCreateForm";
import { UserStatusType } from "@/types/db/users";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import useAuth from "@/hooks/useAuth";
import { userService } from "@/services/userService";
import { supabase } from "@/lib/supabase-client";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import Head from "expo-router/head";
import { getSaveBehavior } from "@/lib/saveSettings";

type UserMeta = UserForm["meta_data"];

export default function NewUserPage() {
  const toast = useToast();
  const router = useRouter();
  const { colors } = useTheme();
  const { tenantId, session } = useAuth();
  const { triggerRefresh } = useRowSelectionContext();
  const [userMeta, setUserMeta] = useState<UserMeta | null>(null);
  const [localDraft, setLocalDraft] = useState<UserPayload>();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [status, setStatus] = useState<UserStatusType>("ONBOARDING");
  const [dirty, setDirty] = useState(false);
  const formRef = useRef<UserCreateFormRef>(null);

  const handleCreateUser = async (payload: UserPayload) => {
    try {
      if (!tenantId) throw new Error("No tenant ID available");
      if (!session?.access_token) throw new Error("No access token available");

      console.log("Attempting to create user with payload:", {
        ...payload.user,
        password: payload.user.password ? "[REDACTED]" : undefined,
      });

      // Call edge function to create auth user first, then DB user
      const result = await userService.createUserWithAuth(payload.user, session.access_token);

      // Sync roles if the user was created and roles were assigned
      if (result.user?.id && payload.user.user_roles && payload.user.user_roles.length > 0) {
        const roleIds = payload.user.user_roles.map((ur) => ur.role.id);
        await userService.syncUserRoles(result.user.id, roleIds);
      }

      // Sync positions if the user was created and positions were assigned
      if (result.user?.id && payload.user.user_positions && payload.user.user_positions.length > 0) {
        const positionIds = payload.user.user_positions.map((ur) => ({
          position: { id: ur.position.id },
          meta_data: ur.meta_data ?? {},
        }));
        await userService.syncUserPositions(result.user.id, positionIds as []);
      }

      toast.showToast({ title: "User created successfully", toastType: "success" });
      triggerRefresh("user");

      // Handle save behavior setting
      const behavior = await getSaveBehavior("user");
      switch (behavior) {
        case "view_created":
          router.setParams({ new: undefined, id: result.user?.id });
          break;
        case "create_another":
          handleReset();
          break;
        case "close":
        default:
          router.setParams({ new: undefined, panel: undefined });
          break;
      }
    } catch (err: any) {
      console.error("User creation failed:", err);
      toast.showToast({
        title: "Failed to create user",
        message: err.message || "Unknown error occurred",
        toastType: "error",
      });
    }
  };

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data, error } = await supabase.from("tenants").select("meta_data").eq("id", tenantId).single();
      if (error) throw error;
      setUserMeta((data as any)?.meta_data ?? null);
    } catch (e: any) {
      setErr(e?.message ?? "Failed to load user settings.");
      toast.showToast({ title: "Failed to fetch tenant data", message: e.message, toastType: "error" });
      setUserMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync firstName and lastName with form state
  useEffect(() => {
    if (localDraft?.user?.first_name !== undefined && localDraft.user.first_name !== firstName) {
      setFirstName(localDraft.user.first_name);
    }
    if (localDraft?.user?.last_name !== undefined && localDraft.user.last_name !== lastName) {
      setLastName(localDraft.user.last_name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDraft?.user?.first_name, localDraft?.user?.last_name]);

  const handleReset = () => {
    setFirstName("");
    setLastName("");
    setStatus("ONBOARDING");
    formRef.current?.handleReset();
  };

  if (loading) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText>Loading user settings…</ThemedText>
      </ScrollView>
    );
  }

  return (
    <>
      <Head>
        <title>NEXUS New User</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="user"
          useFirstLastName
          firstName={firstName}
          lastName={lastName}
          onFirstNameChange={(name) => {
            setFirstName(name);
            formRef.current?.updateFirstName(name);
          }}
          onLastNameChange={(name) => {
            setLastName(name);
            formRef.current?.updateLastName(name);
          }}
          dirty={dirty}
          onSave={async () => {
            await formRef.current?.handleSave();
          }}
          onCancel={handleReset}
          status={status}
          onStatusChange={(value) => {
            setStatus(value as UserStatusType);
            formRef.current?.updateStatus(value);
          }}
          statusOptions={USER_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getUserStatusColor(opt.text, colors),
          }))}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <UserCreateForm
            ref={formRef}
            initialUser={{
              ...userMeta,
              first_name: firstName,
              last_name: lastName,
              meta_data: { ...userMeta, status, user_type: "employee" as const },
            }}
            onChange={(current) => {
              setLocalDraft(current);
            }}
            onSave={handleCreateUser}
            onDirtyChange={setDirty}
          />
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  centered: {
    paddingVertical: 32,
    alignItems: "center",
    gap: 8,
  },
});
